<template>
<div>
	<form class="container profile-form">
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('name')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<input class="form-control" v-model="profileData.name" :disabled="!isEditing" />
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('email')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<input class="form-control" v-model="profileData.email"  :disabled="!isEditing"/>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('phone_number')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<vue-phone-input
					ref="phone-input"
					name="phone"
					:disabled="true"
					:value="profileData.phone_number"
					:code="countryCode"
					:onChange="onPhoneNumberChange"
					:placeholder="$t('message.customer_phone_placeholder')"
				/>
				<div v-if="!profileData.sms_verified" class="label label-tight label-muted">
					{{$t('bookingTable.sms_not_verified')}}
				</div>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('gender')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div class="flex-horizontal-center form-select-row" v-if="isEditing">
					<select class="form-control w-100" v-model="profileData.gender">
						<option v-for="o in genderOptions" :value="o.value">{{ $t(`general.${o.label}`) }}</option>
					</select>
				</div>
				<input class="form-control" :value="$t(`general.${genderLabel}`)"  :disabled="!isEditing" v-else/>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('age_group')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div class="flex-horizontal-center form-select-row" v-if="isEditing">
					<select class="form-control w-100" v-model="profileData.age_group">
						<option v-for="o in ageGroupOptions" :value="o.value">{{ $t(`general.${o.label}`) }}</option>
					</select>
				</div>
				<input class="form-control" :value="$t(`general.${ageGroupLabel}`)"  :disabled="!isEditing" v-else/>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('account_integration')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div class="row">
					<template v-if="hasBindFacebook || hasBindLine">
						<binding-information
							v-if="hasBindFacebook"
							:type="Constants.BindingPlatform.FACEBOOK"
							:is-editing="isEditing"
							:name="profileData.facebook_name"
							:facebook-id="profileData.facebook_id"
							class="col-12 col-lg-auto"
							@unbind="unbind"
						/>
						<binding-information
							v-if="hasBindLine"
							:type="Constants.BindingPlatform.LINE"
							:is-editing="isEditing"
							:name="profileData.line_name"
							:line-image-url="profileData.line_image_url"
							class="col mt-3 mt-lg-0"
							@unbind="unbind"
						/>
					</template>

					<div v-else class="col">
						{{$t('none')}}
					</div>
				</div>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('level')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<span v-if="profileData.level" class="level-tag rounded">
					{{ profileData.level.label }}
				</span>
				<span v-else>
					{{$t('none')}}
				</span>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('level_description')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div v-if="profileData.level">
					{{ profileData.level.description }}
				</div>
				<div v-else>
					{{$t('none')}}
				</div>
			</div>
		</div>
		<div class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('note')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div v-if="profileData.note_to_customer">
					{{ profileData.note_to_customer}}
				</div>
				<div v-else>
					{{$t('none')}}
				</div>
			</div>
		</div>
		<div v-if="pointModuleIsEnabled && canUseMemberPointsModule" class="row mb-2">
			<div class="col-12 col-md-4 col-lg-3">{{$t('points')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				{{ profileData.points }}
			</div>
		</div>
		<div class="row mb-2" v-if="isEditing">
			<div class="col-12">
				<hr />
			</div>
			<div class="col-12 col-md-4 col-lg-3">{{$t('set_new_password')}}</div>
			<div class="col-12 col-md-8 col-lg-9">
				<div>
					<input v-model="newPassword" class="form-control" type="password" autocomplete="new-password" :placeholder="$t('enter_new_password')" />
				</div>
				<div class="mt-2">
					<input v-model="newPasswordConfirmation" class="form-control" type="password" :placeholder="$t('enter_new_password_again_to_confirm')" />
				</div>
			</div>
			<div class="col-12">
				<hr />
			</div>
		</div>
		<div class="row mb-2 mt-4">
			<div class="col-12 text-center">
				<button v-if="isEditing" class="btn btn-primary" @click.prevent.stop="save">{{$t('save')}}</button>
				<button v-else class="btn btn-primary" @click.prevent.stop="isEditing = true">{{$t('edit')}}</button>

				<button v-if="isEditing" class="btn btn-secondary" @click.prevent.stop="cancelEditing">{{$t('cancel')}}</button>
			</div>
			<div class="col-12 text-center my-3">
				<button class="btn btn-secondary" type="button" @click.stop="logOut">{{ $t('general.logout') }}</button>
			</div>
		</div>
	</form>
	<loading-indicator :isLoading="isLoading" />
</div>

</template>

<script>
import { mapActions, mapGetters } from "vuex"
import LoadingIndicator from './common/LoadingIndicator.vue';
import vuePhoneInput from './vue-phone-input/index.vue';
import promptAlert from '../mixins/prompt-confirm';
import api from "../mixins/api";
import CapabilityManager from "../bu/CapabilityManager";
import { genderOptions, ageGroupOptions } from '../../data/form-select-options.json'
import Constants from "../constants";
import BindingInformation from './common/BindingInformation.vue';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

export default {
	mixins: [promptAlert, api],
	components: { vuePhoneInput, LoadingIndicator, BindingInformation },
	computed: {
		...mapGetters({
			'customer': 'app/customer'
		}),
		genderLabel() {
			return  this.profileData.gender === undefined ? this.genderOptions[0].label : this.genderOptions.find(o => o.value === this.profileData.gender).label
		},
		ageGroupLabel() {
			return !this.profileData.age_group ? this.ageGroupOptions[0].label : this.ageGroupOptions.find(o => o.value === this.profileData.age_group).label
		},
		hasBindFacebook() {
			return this.profileData.facebook_id && this.unbindPlatforms.indexOf('facebook') === -1;
		},
		hasBindLine() {
			return this.profileData.line_user_id && this.unbindPlatforms.indexOf('line') === -1;
		},
		hasValidName() {
			return this.profileData.name && this.profileData.name.length >= 2;
		},
		hasValidEmail() {
			return this.profileData.email && this.profileData.email.match(Constants.validator.email);
		},
		countryCode() {
            const countries = getCountries();
            return countries.find((country) => {
                return getCountryCallingCode(country) === this.profileData.phone_calling_code;
            }) || 'tw';
        },
	},
	props: {
		context: { type: Object, required: true },
		provider: { type: Object, required: true }
	},
	data() {
		const capabilityManager = new CapabilityManager(this.provider.grade, this.provider.capabilities);

		return {
			canUseMemberPointsModule: capabilityManager.canManageMemberPoints(),
			profileData: {},
			isEditing: false,
			isLoading: false,
			newPasswordConfirmation: '',
			newPassword: '',
			pointModuleIsEnabled: false,
			genderOptions,
			ageGroupOptions,
			unbindPlatforms: [],
			Constants,
		}
	},
	mounted() {
		this.preset();
		const close = this.promptLoading();
		this.getPointSetting((response, error) => {
			close();
			if (error) {
				this.promptAlert(error);
			} else {
				this.pointModuleIsEnabled = response.data.is_enabled;
			}
		});
	},
	watch: {
		customer() {
			this.preset()
		}
	},
	methods: {
		...mapActions({
			logOutCustomer: 'app/logOutCustomer',
			updateLoggedInCustomer: 'app/updateLoggedInCustomer'
		}),
		preset() {
			this.profileData = Object.assign({}, this.customer);
			this.$refs['phone-input']?.setPhoneNumber(this.customer.phone_number);
		},
		onPhoneNumberChange(phoneInputComponent) {
			this.profileData.phone_number = phoneInputComponent.phoneNumber;
			this.profileData.phone_calling_code = new String(phoneInputComponent.availableData[phoneInputComponent.countryCode].dialCode);
		},
		async logOut() {
			await this.logOutCustomer();
			window.location = '/';
		},
		async save() {
			if (!this.hasValidName) {
				this.promptAlert(this.$t('message.please_fill_your_name'));
				return;
			}
			if (!this.hasValidEmail) {
				this.promptAlert(this.$t('message.please_correct_your_email'));
				return;
			}
			try {
				var payload = {
					name: this.profileData.name,
					email: this.profileData.email,
					gender: this.profileData.gender,
					ageGroup: this.profileData.age_group,
				};

				if (this.newPassword || this.newPasswordConfirmation) {
					if (this.newPassword !== this.newPasswordConfirmation) {
						this.promptAlert(this.$t('password_confirmation_not_matched'), () => {
							this.newPassword = '';
							this.newPasswordConfirmation = '';
						});
						return;
					}
					if (!this.newPassword.match(/^[a-zA-Z0-9]{6,}$/)) {
						this.promptAlert(this.$t('password_should_be_6_digits_alpha_numeric'));
						return;
					}
					payload.password = this.newPassword;
				}

				if (this.unbindPlatforms.length) {
					payload.unbind_platforms = this.unbindPlatforms.join(',');
				}

				this.isLoading = true;
				await this.updateLoggedInCustomer(payload);
				this.promptAlert(this.$t('saved'), () => {
					this.isEditing = false;
				}, 'success');
			} catch (e) {
				this.promptAlert(this.$t('fail_to_update'));
			} finally {
				this.isLoading = false;
			}
		},
		cancelEditing() {
			this.preset();
			this.isEditing = false;
			this.newPassword = '';
			this.newPasswordConfirmation = '';
			this.unbindPlatforms = [];
		},
		unbind(platform) {
			if (this.unbindPlatforms.indexOf(platform) === -1) {
				this.unbindPlatforms.push(platform);
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.profile-form {
	margin: 50px auto;

	.row {
		line-height: 38px;
	}
}

.fb-avatar-icon,
.line-avatar-icon {
	border-radius: 20px;
}

.level-tag {
	display: inline-flex;
	height: 28px;
	padding-left: 0.4em;
	padding-right: 0.4em;
	align-items: center;
}

</style>

<i18n>
{
	"en": {
		"name": "Name",
		"email": "Email",
		"phone_number": "Phone Number",
		"gender": "Gender",
		"age_group": "Age Group",
		"account_integration": "Account Integration",
		"points": "MemberPoints",
		"unbind": "Unbind",
		"edit": "Edit",
		"save": "Save",
		"cancel": "Cancel",
		"none": "None",
		"fail_to_update": "Fail to update data.",
		"saved": "Profile saved",
		"set_new_password": "Set New Password (optional)",
		"enter_new_password": "Enter new password(6 digits more alpha-number)",
		"enter_new_password_again_to_confirm": "Enter new password again to confirm",
		"password_confirmation_not_matched": "New password confirmation not matched, please re-enter new password.",
		"password_should_be_6_digits_alpha_numeric": "Password should be 6 digits more alpha-number",
		"note": "Note",
		"level": "Member Level",
		"level_description": "Member Level Description"
	},
	"zh": {
		"name": "姓名",
		"email": "電子郵件",
		"phone_number": "手機號碼",
		"gender": "性別",
		"age_group": "年齡",
		"account_integration": "帳戶綁定",
		"points": "會員點數",
		"unbind": "解除綁定",
		"edit": "編輯",
		"save": "儲存",
		"cancel": "取消",
		"none": "無",
		"fail_to_update": "無法更新資料！",
		"saved": "更新完成",
		"set_new_password": "設定新密碼(不改可不填)",
		"enter_new_password": "輸入新密碼（6 碼以上英數）",
		"enter_new_password_again_to_confirm": "再次輸入新密碼已確認",
		"password_confirmation_not_matched": "新密碼兩次輸入不同，重新輸入",
		"password_should_be_6_digits_alpha_numeric": "密碼須為 6碼以上英數",
		"note": "註記",
		"level": "會員等級",
		"level_description": "會員等級說明"
	}
}
</i18n>
